import {Component, inject} from '@angular/core';
import {NgbDate, NgbDatepicker, NgbDatepickerModule, NgbDateStruct} from "@ng-bootstrap/ng-bootstrap";
import {Location, NgClass} from "@angular/common";
import {ReactiveFormsModule} from "@angular/forms";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {Samples} from "src/app/infrastructure/moovement-api/models/samples";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {AssetSampleApiClient} from "src/app/infrastructure/moovement-api/web-api/asset-sample.api-client";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {MapStore} from 'src/app/domain/map/map.store';
import {
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {twentyFourHours} from 'src/app/presentation/common/globals';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';

@Component({
  selector: 'app-grazing-patterns',
  imports: [
    GenericButtonComponent,
    NgbDatepicker,
    NgbDatepickerModule,
    ReactiveFormsModule,
    NgClass
  ],
  templateUrl: './grazing-patterns.component.html',
  styleUrl: './grazing-patterns.component.css'
})
export class GrazingPatternsComponent {

  heatmapShown: boolean = false;
  hoveredDate: NgbDate | null = null;
  fromDate?: NgbDate;
  toDate?: NgbDate | null;
  loading = false;

  private animalsStore = inject(AnimalsStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);
  private location = inject(Location);
  private mainViewStore = inject(MainViewStore);
  private mapStore = inject(MapStore);

  private currentDate: Date = new Date();
  today: NgbDateStruct = {
    year: this.currentDate.getFullYear(), month: this.currentDate.getMonth() + 1, day: this.currentDate.getDate()
  };

  constructor(
    protected dataSubscriptionService: DataSubscriptionService,
    protected viewportService: ViewportService,
    private assetSampleService: AssetSampleApiClient
  ) {}

  generateHeatmap() {
    const animals = this.animalsStore.entities();
    if (this.heatmapShown || !this.fromDate || animals.length === 0) return;

    this.dataSubscriptionService.publish('grazingPatternSamples', []);

    this.loading = true

    const fromDate = new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day)
    const toDate = this.toDate ?
      new Date(this.toDate.year, this.toDate.month - 1, this.toDate.day + 1) :
      new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day + 1);

    const animalIds = animals.map(animal => animal.id!)
    this.assetSampleService.getAssetSamplesBetween<Samples.GPSFix>('Location', animalIds, fromDate, toDate).subscribe({
      next: (locationAssetSamples) => {
        if (locationAssetSamples.length > 0) {
          this.dataSubscriptionService.publish('grazingPatternSamples', locationAssetSamples)
          this.mapStore.showGrazingPatternLayer();
          this.mapStore.hideGatewayLayer();
          this.mapStore.hideWaterPointLayer();
          this.mapStore.hideAnimalLocationLayer();
          this.mapStore.hideHistoricAnimalLocationLayer();
          this.mapStore.hidePrecipitationSamplePointLayer();
          this.mapStore.hideProximitySamplePointLayer();
          this.heatmapShown = true
        } else {
          this.internalNotificationsStore.addNotification("There's no grazing data available for the selected period.", InternalNotificationType.INFO, false);
          this.mapStore.hideGrazingPatternLayer();
          this.mapStore.showGatewayLayer();
          this.mapStore.showWaterPointLayer();
          this.mapStore.showAnimalLocationLayer();
          this.mapStore.showPrecipitationSamplePointLayer();
          this.mapStore.showProximitySamplePointLayer();
        }
        this.loading = false
      },
      error: () => {
        this.internalNotificationsStore.addNotification("An unexpected error occured while retrieving grazing data.", InternalNotificationType.ERROR, false);
        this.loading = false;
      }
    });
  }

  onDateSelection(date: NgbDate) {
    if (this.heatmapShown) {
      this.reset()
    }
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  isValid(date: NgbDate) {
    if (date.before(this.fromDate!)) return false;

    const from = new Date(this.fromDate!.year, this.fromDate!.month - 1, this.fromDate!.day);
    const to = new Date(date.year, date.month - 1, date.day + 1);
    const deltaDays = (to.getTime() - from.getTime()) / twentyFourHours;

    return deltaDays <= 8;
  }

  isInHoveredRange(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  isInSelectedRange(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isValidRange() {
    return (this.fromDate && this.toDate) || (this.hoveredDate && this.isValid(this.hoveredDate));
  }

  isStartOrEndOfRange(date: NgbDate) {
    return date.equals(this.fromDate) ||
      date.equals(this.toDate);
  }

  reset() {
    this.mapStore.resetLayers();
    this.heatmapShown = false
  }

  back(): void {
    this.location.back();
    this.reset();
  }

  close() {
    this.mainViewStore.hideChildComponent();
  }
}
